// src/views/menage/RecapsSemaine.jsx
//
// Les deux récapitulatifs ouverts depuis la colonne de droite (maquette validée
// Dimitri, 2026-08-10 soir).
//
// RÉCAP D'UN AGENT — ce qu'il a fait, ce qui lui reste, et surtout SES HEURES DE
// TRAVAIL. C'est le chiffre qui manquait le plus : « 3 à faire / 3 faits » dit
// combien de ménages, jamais combien de temps — et une journée de six ménages
// courts n'a rien à voir avec une journée de trois longs.
//
// RÉCAP « SANS AGENT » — orienté ACTION, pas constat. Il ne se contente pas de
// lister ce qui n'est pas assigné : il dit QUI EST LIBRE pour le prendre, et
// montre les agents complets en grisé plutôt que de les masquer (les masquer
// ferait croire qu'ils n'existent pas, et on les chercherait).

import { useMemo } from "react"
import { DS } from "../../lib/ui"
import {
  formaterHeure, formaterDuree, heuresDeTravail, agentsDisponibles, etatBloc,
} from "../../lib/menage-frise"
import { libelleDepuisEmail, initialsFromEmail } from "./RunCard"
import { X, CircleCheck, AlertTriangle, Star, Wand2 } from "lucide-react"
import useEchapPourFermer from "../../lib/useEchapPourFermer"

const C = {
  gris: '#3d5166', fonce: '#2e3d4d', ciel: '#87b8e6', cielPale: '#e4f0fb',
  cielTexte: '#1c2833', bord: '#d5e3ee', blocGris: '#f6f9fc',
  orphFond: '#fdf4f0', orphAccent: '#c1512d',
  retardFond: '#fbf3f1', retardTexte: '#8f2f1d',
  vertTexte: '#2c7a5c', inter: '#7b6bb0',
}

function Coquille({ titre, sousTitre, pastille, onFermer, children, testId }) {
  // Échap ferme — consigne 2026-08-14, hook partagé (pile anti-double-fermeture).
  useEchapPourFermer(onFermer)
  return (
    <div role="dialog" aria-label={titre} data-testid={testId}
      style={{position:'fixed',inset:0,background:'rgba(46,61,77,.42)',zIndex:60,
        display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'#fff',borderRadius:14,maxWidth:520,width:'100%',maxHeight:'88vh',
        overflow:'hidden',display:'flex',flexDirection:'column',fontFamily:DS.fontBody}}>
        <div style={{background:C.gris,color:'#fff',padding:'15px 18px',display:'flex',
          alignItems:'center',gap:11}}>
          {pastille}
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontFamily:DS.fontDisplay,fontSize:17,fontWeight:600,lineHeight:1.2}}>{titre}</div>
            <div style={{fontSize:12,color:'rgba(255,255,255,.8)',marginTop:2}}>{sousTitre}</div>
          </div>
          <button type="button" onClick={onFermer} aria-label="Fermer"
            style={{background:'none',border:'none',cursor:'pointer',color:'rgba(255,255,255,.85)',padding:2}}>
            <X size={19} strokeWidth={2} />
          </button>
        </div>
        <div style={{overflowY:'auto',padding:'16px 18px',display:'flex',flexDirection:'column',gap:14}}>
          {children}
        </div>
      </div>
    </div>
  )
}

function Compteur({ valeur, libelle, alerte = false }) {
  return (
    <div style={{flex:1,minWidth:96,background:C.blocGris,borderRadius:10,padding:'11px 13px'}}>
      <div style={{fontFamily:DS.fontDisplay,fontSize:22,fontWeight:600,lineHeight:1,
        color: alerte ? C.retardTexte : C.gris}}>{valeur}</div>
      <div style={{fontSize:11,color:DS.textMuted,marginTop:4}}>{libelle}</div>
    </div>
  )
}

function libelleJour(iso) {
  const d = new Date(`${iso}T12:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  const s = new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }).format(d)
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// ── RÉCAP D'UN AGENT ────────────────────────────────────────────────────────

export function RecapAgent({
  email, blocsSemaine, libelleSemaine, avisParLogement, incidents, estEnRetard, onFermer,
}) {
  const nom = libelleDepuisEmail(email)

  const { aFaire, faits, minutes } = useMemo(() => {
    let a = 0
    let f = 0
    for (const b of blocsSemaine) {
      if (b.termine) f += 1
      else a += 1
    }
    return { aFaire: a, faits: f, minutes: heuresDeTravail(blocsSemaine) }
  }, [blocsSemaine])

  const parJour = useMemo(() => {
    const m = new Map()
    for (const b of blocsSemaine) {
      const j = b.run?.scheduled_date || b.intervention?.scheduled_date || '—'
      if (!m.has(j)) m.set(j, [])
      m.get(j).push(b)
    }
    return [...m.entries()].sort((x, y) => String(x[0]).localeCompare(String(y[0])))
  }, [blocsSemaine])

  // Note de propreté SUR SES LOGEMENTS — pas la note globale du parc : c'est
  // son travail qu'on regarde, pas celui de l'équipe.
  const proprete = useMemo(() => {
    const ids = [...new Set(blocsSemaine.map((b) => b.logementId).filter(Boolean))]
    const notes = ids.map((id) => avisParLogement?.[id]?.cleanliness ?? avisParLogement?.[id]?.avg)
      .filter((n) => typeof n === 'number')
    if (!notes.length) return null
    return Math.round((notes.reduce((s, n) => s + n, 0) / notes.length) * 10) / 10
  }, [blocsSemaine, avisParLogement])

  const nbIncidents = useMemo(
    () => (incidents || []).filter((i) => i?.reported_by === email).length,
    [incidents, email]
  )

  return (
    <Coquille
      testId={`recap-agent-${email}`}
      titre={nom}
      sousTitre={libelleSemaine}
      onFermer={onFermer}
      pastille={
        <span style={{width:34,height:34,borderRadius:10,background:'rgba(255,255,255,.15)',
          color:'#fff',fontFamily:DS.fontDisplay,fontWeight:600,fontSize:13,display:'inline-flex',
          alignItems:'center',justifyContent:'center',flexShrink:0}}>
          {initialsFromEmail(email)}
        </span>
      }>
      <div style={{display:'flex',gap:9,flexWrap:'wrap'}}>
        <Compteur valeur={aFaire} libelle="à faire" />
        <Compteur valeur={faits} libelle="faits" />
        {/* ⚠️ LE CHIFFRE QUI MANQUAIT : le TEMPS, pas le nombre. */}
        <Compteur valeur={formaterDuree(minutes)} libelle="de travail" />
      </div>

      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {parJour.length === 0 && (
          <div style={{fontSize:12,color:DS.textMuted}}>Rien cette semaine.</div>
        )}
        {parJour.map(([jour, list]) => (
          <div key={jour}>
            <div style={{fontSize:10.5,fontWeight:700,color:DS.textMuted,textTransform:'uppercase',
              letterSpacing:'.08em',marginBottom:5}}>{libelleJour(jour)}</div>
            <div style={{display:'flex',flexDirection:'column',gap:5}}>
              {list.map((b) => {
                const retard = b.run ? estEnRetard(b.run) : false
                const etat = etatBloc(b, { enRetard: retard })
                const carre = etat === 'intervention' ? C.inter : C.ciel
                return (
                  <div key={b.id} data-testid={`recap-ligne-${b.id}`}
                    style={{display:'flex',alignItems:'center',gap:9,padding:'8px 10px',
                      borderRadius:9,border:`1px solid ${retard ? C.retardTexte : C.bord}`,
                      background: retard ? C.retardFond : '#fff',
                      opacity: b.termine ? .55 : 1}}>
                    {b.termine ? (
                      <CircleCheck size={14} strokeWidth={2.4} color={C.vertTexte} style={{flexShrink:0}} />
                    ) : retard ? (
                      <AlertTriangle size={14} strokeWidth={2.4} color={C.retardTexte} style={{flexShrink:0}} />
                    ) : (
                      <span style={{width:12,height:12,borderRadius:3,background:carre,flexShrink:0}} />
                    )}
                    <span style={{flex:1,minWidth:0,fontSize:12,fontWeight:600,color:C.gris,
                      overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{b.nom}</span>
                    <span style={{fontSize:11,color:DS.textSecondary,flexShrink:0,
                      fontVariantNumeric:'tabular-nums'}}>
                      {formaterHeure(b.debut)} · {formaterDuree(b.dureeMin)}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      <div style={{display:'flex',gap:9,flexWrap:'wrap',borderTop:`1px solid ${C.bord}`,paddingTop:12}}>
        <div style={{flex:1,minWidth:130,display:'flex',alignItems:'center',gap:7}}>
          <Star size={14} strokeWidth={2.2} color="#EF9F27" fill="#EF9F27" />
          <span style={{fontSize:12,color:DS.textSecondary}}>
            {proprete != null
              ? <>Propreté <strong style={{color:C.gris}}>{String(proprete).replace('.', ',')}/5</strong> sur ses logements</>
              : 'Aucun avis sur ses logements'}
          </span>
        </div>
        <div style={{flex:1,minWidth:130,display:'flex',alignItems:'center',gap:7}}>
          <AlertTriangle size={14} strokeWidth={2.2} color={C.gris} />
          <span style={{fontSize:12,color:DS.textSecondary}}>
            {nbIncidents} incident{nbIncidents > 1 ? 's' : ''} signalé{nbIncidents > 1 ? 's' : ''}
          </span>
        </div>
      </div>
    </Coquille>
  )
}

// ── RÉCAP « SANS AGENT » — orienté ACTION ───────────────────────────────────

export function RecapSansAgent({
  blocsSansAgent, tousLesBlocs, agents, regles, nomAgent, arriveeDe,
  onAssigner, onAppliquerRegles, onFermer,
}) {
  const total = heuresDeTravail(blocsSansAgent)

  // Une règle existe-t-elle sans avoir été appliquée ? Le dire est plus utile
  // que de proposer un bouton dont on ne comprend pas l'effet.
  const reglesApplicables = useMemo(
    () => blocsSansAgent.filter((b) => regles?.[b.logementId]?.provider_email),
    [blocsSansAgent, regles]
  )

  return (
    <Coquille
      testId="recap-sans-agent"
      titre="Sans agent"
      sousTitre={`${blocsSansAgent.length} ménage${blocsSansAgent.length > 1 ? 's' : ''} · ${formaterDuree(total)} à répartir`}
      onFermer={onFermer}
      pastille={
        <span style={{width:34,height:34,borderRadius:10,background:'rgba(255,255,255,.15)',
          color:'#fff',fontSize:15,display:'inline-flex',alignItems:'center',
          justifyContent:'center',flexShrink:0}}>⚠</span>
      }>
      {blocsSansAgent.length === 0 && (
        <div style={{fontSize:12.5,color:DS.textMuted}}>Tout est assigné.</div>
      )}

      {blocsSansAgent.map((b) => {
        const dispo = agentsDisponibles(tousLesBlocs, agents.map((a) => a.value), { debut: b.debut, fin: b.fin })
        const regle = regles?.[b.logementId]?.provider_email
        const arrivee = arriveeDe ? arriveeDe(b) : null
        return (
          <div key={b.id} data-testid={`recap-sans-agent-${b.id}`}
            style={{border:`1px solid ${C.bord}`,borderRadius:11,padding:'12px 13px',
              display:'flex',flexDirection:'column',gap:9,background:C.orphFond}}>
            <div style={{display:'flex',alignItems:'baseline',gap:8,flexWrap:'wrap'}}>
              <span style={{fontFamily:DS.fontDisplay,fontSize:14,fontWeight:600,color:C.gris}}>{b.nom}</span>
              <span style={{fontSize:11.5,color:DS.textSecondary,fontVariantNumeric:'tabular-nums'}}>
                plage {formaterHeure(b.creneau.debut)} → {formaterHeure(b.creneau.fin)} · ménage {formaterDuree(b.dureeMin)}
              </span>
            </div>

            <div style={{fontSize:11.5,color:DS.textSecondary}}>
              {arrivee
                ? <>Ensuite : arrivée à <strong style={{color:C.gris}}>{formaterHeure(arrivee.minutes)}</strong>{arrivee.source === 'logement' ? ' (horaire du logement)' : ' (annoncée)'}</>
                : 'Personne n’arrive après.'}
            </div>

            {/* QUI EST LIBRE — les complets restent visibles, grisés. */}
            <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
              <span style={{fontSize:11,color:DS.textMuted}}>Qui est libre :</span>
              {dispo.map(({ email, libre }) => (
                <button key={email} type="button" disabled={!libre}
                  data-testid={`recap-assigner-${b.id}-${email}`}
                  onClick={() => onAssigner(b, email)}
                  title={libre ? `Confier à ${nomAgent(email)}` : `${nomAgent(email)} travaille déjà à cette heure-là`}
                  style={{display:'inline-flex',alignItems:'center',gap:5,borderRadius:7,
                    padding:'5px 9px',fontSize:11,fontWeight:700,fontFamily:DS.fontBody,
                    border:`1px solid ${libre ? C.ciel : C.bord}`,
                    background: libre ? C.ciel : '#fff',
                    color: libre ? C.cielTexte : DS.textMuted,
                    cursor: libre ? 'pointer' : 'not-allowed',
                    opacity: libre ? 1 : .65}}>
                  {initialsFromEmail(email)} · {nomAgent(email)}{libre ? '' : ' — complet'}
                </button>
              ))}
            </div>

            {regle && (
              <div style={{fontSize:11,color:DS.textSecondary,lineHeight:1.5}}>
                Une règle désigne <strong style={{color:C.gris}}>{nomAgent(regle)}</strong> pour ce
                logement — elle ne s’applique qu’aux ménages créés après sa pose, ce qui n’est pas
                le cas de celui-ci.
              </div>
            )}
          </div>
        )
      })}

      {reglesApplicables.length > 0 && (
        <button type="button" data-testid="recap-appliquer-regles"
          onClick={() => onAppliquerRegles(reglesApplicables)}
          style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:7,
            background:C.gris,color:'#fff',border:'none',borderRadius:DS.radiusControl,
            padding:'11px 15px',fontSize:12.5,fontWeight:700,cursor:'pointer',
            fontFamily:DS.fontBody}}>
          <Wand2 size={15} strokeWidth={2} />
          Appliquer les règles aux {reglesApplicables.length} ménage{reglesApplicables.length > 1 ? 's' : ''}
        </button>
      )}
    </Coquille>
  )
}
